import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { store } from './app/store';
import { useAppSelector } from './app/hooks';
import HomeDashboard from './pages/home/HomeDashboard';
import AiGeneratorPage from './pages/ai-generator/AiGeneratorPage';
import BrandKitPage from './pages/brand-kit/BrandKitPage';
import CreateBrandKitLogo from './pages/brand-kit/CreateBrandKitLogo';
import CreateBrandKitColors from './pages/brand-kit/CreateBrandKitColors';
import CreateBrandKitTypography from './pages/brand-kit/CreateBrandKitTypography';
import MyProjectsPage from './pages/my-projects/MyProjectsPage';
import { LoginPage } from './pages/login/LoginPage';
import TemplatesPage from './pages/templates/TemplatesPage';
import TemplateEditorPage from './pages/template-editor/TemplateEditorPageWrapper';
import PreviewPage from './pages/template-editor/PreviewPage';
import AuthHandoffPage from './pages/auth/AuthHandoffPage';
import { MainLayout } from './components/shared/MainLayout';
import IntegrationsPage from './pages/integrations/IntegrationsPage';

import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

// To prevent "Cannot have two HTML5 backends at the same time" error
const DndProviderWrapper = ({ children }: { children: React.ReactNode }) => {
  return (
    <DndProvider backend={HTML5Backend} options={{ allowUndefinedBackend: true }}>
      {children}
    </DndProvider>
  );
};

// Main App Component
const AppContent = () => {
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);

  const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
    return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
  };

  return (
    <Routes>
      <Route path="/login" element={isAuthenticated ? <Navigate to="/home" replace /> : <LoginPage />} />
      <Route path="/auth/handoff" element={<AuthHandoffPage />} />
      <Route path="/" element={<Navigate to="/home" replace />} />

      {/* Protected Routes with Sidebar Layout */}
      <Route path="/home" element={<ProtectedRoute><MainLayout><HomeDashboard /></MainLayout></ProtectedRoute>} />
      <Route path="/ai-generator" element={<ProtectedRoute><MainLayout><AiGeneratorPage /></MainLayout></ProtectedRoute>} />
      <Route path="/brand-kit" element={<ProtectedRoute><MainLayout><BrandKitPage /></MainLayout></ProtectedRoute>} />
      <Route path="/brand-kit/create/logo" element={<ProtectedRoute><MainLayout><CreateBrandKitLogo /></MainLayout></ProtectedRoute>} />
      <Route path="/brand-kit/create/colors" element={<ProtectedRoute><MainLayout><CreateBrandKitColors /></MainLayout></ProtectedRoute>} />
      <Route path="/brand-kit/create/typography" element={<ProtectedRoute><MainLayout><CreateBrandKitTypography /></MainLayout></ProtectedRoute>} />
      <Route path="/brand-kit/edit/:id" element={<Navigate to="logo" replace />} />
      <Route path="/brand-kit/edit/:id/logo" element={<ProtectedRoute><MainLayout><CreateBrandKitLogo /></MainLayout></ProtectedRoute>} />
      <Route path="/brand-kit/edit/:id/colors" element={<ProtectedRoute><MainLayout><CreateBrandKitColors /></MainLayout></ProtectedRoute>} />
      <Route path="/brand-kit/edit/:id/typography" element={<ProtectedRoute><MainLayout><CreateBrandKitTypography /></MainLayout></ProtectedRoute>} />
      <Route path="/my-projects" element={<ProtectedRoute><MainLayout><MyProjectsPage /></MainLayout></ProtectedRoute>} />
      <Route path="/templates" element={<ProtectedRoute><MainLayout><TemplatesPage /></MainLayout></ProtectedRoute>} />
      <Route path="/integrations" element={<ProtectedRoute><MainLayout><IntegrationsPage /></MainLayout></ProtectedRoute>} />
      
      {/* Pages without Sidebar (Editor, Preview) */}
      <Route path="/template-editor" element={<ProtectedRoute><TemplateEditorPage /></ProtectedRoute>} />
      <Route path="/preview/:id" element={<PreviewPage />} />
      
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

import { GlobalErrorHandler } from './components/shared/GlobalErrorHandler';

export default function App() {
  return (
    <Provider store={store}>
      <GlobalErrorHandler>
        <DndProviderWrapper>
          <Suspense fallback={<div className="flex items-center justify-center min-h-screen">Loading...</div>}>
            <AppContent />
          </Suspense>
        </DndProviderWrapper>
      </GlobalErrorHandler>
    </Provider>
  );
}

